import { DownloadOutlined } from "@ant-design/icons";
import { Alert, Button, Collapse, Descriptions, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getSessionDetail } from "../../api/logs.api";
import StoredMessageView from "../../components/monitor/StoredMessageView";
import StatusBadge from "../../components/ui/Badge";
import Card from "../../components/ui/Card";
import Spinner from "../../components/ui/Spinner";
import { formatCost, formatDate, formatMs } from "../../utils/formatters";

const EMPTY = "—";

const cleanIntent = (value?: string | null): string => {
  if (!value) return EMPTY;
  const parts = value.split(".");
  return parts[parts.length - 1];
};

const downloadJson = (data: any) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `session-${data.id?.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

const SessionDetailPage = () => {
  const { sessionId } = useParams();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!sessionId) return;
      try {
        setError(null);
        const { data: response } = await getSessionDetail(sessionId);
        setData(response);
      } catch (_error) {
        setError("Unable to load this session right now.");
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [sessionId]);

  if (loading || !data) {
    return <Spinner />;
  }

  const totalTokensIn = data.chat_logs.reduce(
    (sum: number, log: any) => sum + (log.tokens_input ?? 0),
    0
  );
  const totalTokensOut = data.chat_logs.reduce(
    (sum: number, log: any) => sum + (log.tokens_output ?? 0),
    0
  );

  return (
    <div className="page-stack">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography.Title level={2} style={{ margin: 0 }}>
          Session Detail
        </Typography.Title>
        <Button icon={<DownloadOutlined />} onClick={() => downloadJson(data)}>
          Export JSON
        </Button>
      </div>

      {error ? <Alert type="error" showIcon message={error} /> : null}

      <Card>
        <Descriptions column={{ xs: 1, md: 2, xl: 3 }}>
          <Descriptions.Item label="User">
            {data.user_email ?? "Anonymous"}
          </Descriptions.Item>
          <Descriptions.Item label="Started">
            {formatDate(data.started_at)}
          </Descriptions.Item>
          <Descriptions.Item label="Messages">
            {data.total_messages}
          </Descriptions.Item>
          <Descriptions.Item label="Intent">
            <Tag color="blue">{cleanIntent(data.metadata?.last_intent)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Language">
            {data.metadata?.last_detected_language ?? EMPTY}
          </Descriptions.Item>
          <Descriptions.Item label="Model">
            {data.metadata?.last_model_name ?? EMPTY}
          </Descriptions.Item>
          <Descriptions.Item label="Tokens In">
            {totalTokensIn || EMPTY}
          </Descriptions.Item>
          <Descriptions.Item label="Tokens Out">
            {totalTokensOut || EMPTY}
          </Descriptions.Item>
          <Descriptions.Item label="Cost">
            {formatCost(data.cost_usd)}
          </Descriptions.Item>
          {data.metadata?.location ? (
            <Descriptions.Item label="Location">
              {[
                data.metadata.location.city,
                data.metadata.location.region,
                data.metadata.location.country,
              ]
                .filter(Boolean)
                .join(", ")}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      </Card>

      <div className="chat-replay">
        {data.chat_logs.map((log: any) => (
          <Card key={log.id} className={`chat-log chat-log--${log.role}`}>
            <div className="chat-log__meta">
              <Typography.Text strong>{log.role}</Typography.Text>
              <Typography.Text type="secondary">
                {formatDate(log.timestamp)}
                {log.latency_ms != null ? ` · ${formatMs(log.latency_ms)}` : ""}
                {log.tokens_input != null || log.tokens_output != null
                  ? ` · ${log.tokens_input ?? 0}↑ ${log.tokens_output ?? 0}↓ tokens`
                  : ""}
              </Typography.Text>
            </div>
            <StoredMessageView message={log.message} />
            {log.error_logs.length ? (
              <Collapse
                items={[
                  {
                    key: "errors",
                    label: `Errors (${log.error_logs.length})`,
                    children: (
                      <div>
                        {log.error_logs.map((item: any) => (
                          <div key={item.id} className="error-list-item">
                            <StatusBadge status="failed" />
                            <Typography.Text strong>
                              {item.error_type}
                            </Typography.Text>
                            <Typography.Paragraph>
                              {item.error_message}
                            </Typography.Paragraph>
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            ) : null}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SessionDetailPage;
